'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const { t } = useLanguage();
  const { user, isAdmin, loading } = useAuth();

  const navigationItems = [
    { label: t('header.home'), href: '/home' },
    { label: t('header.aboutUs'), href: '/about-us' },
    { label: t('header.whyGoldSilver'), href: '/why-gold-and-silver' },
    { label: t('header.products'), href: '/products' },
    { label: t('header.priceList'), href: '/price-list' },
    { label: t('header.contact'), href: '/contact' },
  ];

  const isActive = (href: string) => pathname === href;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-navigation bg-white/95 backdrop-blur-md shadow-luxury-md border-b border-border">
        <div className="mx-auto max-w-content">
          <div className="flex h-[80px] items-center justify-between px-6 lg:px-8">
            {/* Logo */}
            <Link 
              href="/home" 
              className="flex items-center transition-luxury hover:opacity-80 group"
              aria-label="Gold Dreams Home"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <AppImage
                    src="/assets/images/2c86d988-ff00-4e5a-8c6d-e1423248a9b8-1769609163212.png"
                    alt="Gold Dreams Logo"
                    width={72}
                    height={72}
                    className="object-contain transition-luxury group-hover:scale-105"
                  />
                </div>
                <span className="font-heading text-xl font-bold tracking-tight text-foreground">
                  GOLD DREAMS
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-2" aria-label="Primary navigation">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`
                    relative px-5 py-2.5 rounded-lg font-body text-[15px] font-medium
                    transition-luxury
                    ${isActive(item.href)
                      ? 'text-accent' : 'text-foreground hover:text-accent'
                    }
                  `}
                >
                  {item.label}
                  {isActive(item.href) && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-gold rounded-full" />
                  )}
                </Link>
              ))}
              {!loading && user && isAdmin() && (
                <Link
                  href="/admin"
                  className={`
                    relative px-5 py-2.5 rounded-lg font-body text-[15px] font-medium
                    transition-luxury
                    ${isActive('/admin')
                      ? 'text-accent' : 'text-foreground hover:text-accent'
                    }
                  `}
                >
                  Admin
                  {isActive('/admin') && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-gold rounded-full" />
                  )}
                </Link>
              )}
              <div className="ml-2">
                <LanguageSwitcher />
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2.5 rounded-lg text-foreground hover:bg-muted transition-luxury"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <Icon 
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} 
                size={24} 
                variant="outline"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-primary/20 backdrop-blur-sm"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          
          {/* Menu Content */}
          <nav 
            className="relative h-full max-w-sm bg-white shadow-luxury-xl overflow-y-auto"
            style={{ animation: 'slideIn 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}
          >
            <div className="flex flex-col p-6">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between mb-10 pb-6 border-b border-border">
                <Link 
                  href="/home" 
                  onClick={closeMobileMenu}
                  className="flex items-center"
                  aria-label="Gold Dreams Home"
                >
                  <AppImage
                    src="/assets/images/2c86d988-ff00-4e5a-8c6d-e1423248a9b8-1769609163212.png"
                    alt="Gold Dreams Logo"
                    width={64}
                    height={64}
                    className="object-contain"
                  />
                </Link>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-lg text-foreground hover:bg-muted transition-luxury"
                  aria-label="Close menu"
                >
                  <Icon name="XMarkIcon" size={24} variant="outline" />
                </button>
              </div>

              {/* Mobile Navigation Items */}
              <div className="flex flex-col gap-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={closeMobileMenu}
                    className={`
                      px-5 py-4 rounded-lg font-body text-base font-medium
                      transition-luxury border
                      ${isActive(item.href)
                        ? 'text-accent bg-accent/5 border-accent/20' : 'text-foreground hover:text-accent hover:bg-muted border-transparent'
                      }
                    `}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Mobile Language Switcher */}
              <div className="mt-8 pt-8 border-t border-border">
                <LanguageSwitcher />
              </div>
            </div>
          </nav>
        </div>
      )}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default Header;